import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FilterGroupingEnum } from 'graphql-common';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { materialSymbolsOutlined } from '@lib/enums/common';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import formStyles from '@lib/assets/styles/Form.module.scss';
import FieldNames from '@lib/enums/fieldNames/teamFieldNames';
import DepartmentsModalTableMultiselect from 'components/DepartmentsModalTableMultiselect/DepartmentsModalTableMultiselect';
import { UseTeamQueriesResult } from 'routes/Teams/utils/useTeamQueries';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import useSearchListQueries from '../utils/useSearchListQueries';
import getTeamFormFields from '../utils/getTeamFormFields';
import { UseTeamActionsResult } from '../utils/useTeamActions';
import { TeamViewMode } from '../enums';
import styles from './TeamFormPage.module.scss';

const SELECT_DEPARTMENTS_MODAL = 'select-departments';

type Props = {
  useModalManagementResult: UseModalManagementResult;
  useTeamActionsResult: UseTeamActionsResult;
  useTeamQueriesResult: UseTeamQueriesResult;
};

export default function TeamFormPage({
  useModalManagementResult,
  useTeamActionsResult,
  useTeamQueriesResult,
}: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const navigate = useNavigate();
  const useSearchListQueriesResult = useSearchListQueries();
  const {
    defaultValues,
    id,
    isCreatePage,
    loading,
    membersIds,
    mutationError,
    onSubmit,
    onViewChange,
  } = useTeamActionsResult;
  const { useTeamQueryHookResult } = useTeamQueriesResult;

  const onDelete = () =>
    useModalManagementResult.openModal(Modals.TeamDestroyModal);

  const isDestroyAvailable = useTeamQueryHookResult.data?.canDestroy?.value;

  const openDepartmentsModalTableMultiselect = useCallback(() => {
    useModalManagementResult.openModal(SELECT_DEPARTMENTS_MODAL);
  }, [useModalManagementResult]);

  const onCloseDepartmentsModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_DEPARTMENTS_MODAL);
  }, [useModalManagementResult]);

  const onDepartmentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FieldNames.DepartmentLinks,
        values.length
          ? values.map((departmentId) => ({ value: departmentId }))
          : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const isDepartmentsModalOpen = useModalManagementResult.isModalOpened(
    SELECT_DEPARTMENTS_MODAL,
  );

  const fields = getTeamFormFields({
    getAssetCategoriesLoadOptions:
      useSearchListQueriesResult.getAssetCategoriesLoadOptions,
    getDocumentationCategoriesLoadOptions:
      useSearchListQueriesResult.getDocumentationCategoriesLoadOptions,
    getInterventionCategoriesLoadOptions:
      useSearchListQueriesResult.getInterventionCategoriesLoadOptions,
    getDepartmentsLoadOptions:
      useSearchListQueriesResult.getDepartmentsLoadOptions,
    getTeamMembersLoadOptions: useSearchListQueriesResult.getUsersLoadOptions,
    membersIds,
    openDepartmentsModalTableMultiselect,
    t,
    teamId: id,
    useTeamQueriesResult,
  });

  const actionsComponent = () => (
    <ActionsRow
      className={formStyles.formActions}
      actions={[
        <Button
          onClick={() => navigate(-1)}
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          leftIcon="close"
          fullWidth
        />,
        <Button
          buttonType={ButtonTypes.primaryFilled}
          buttonText={t(isCreatePage ? 'create' : 'save-changes')}
          leftIcon="check"
          type="submit"
          disabled={loading}
          isLoading={loading}
          fullWidth
        />,
      ]}
    />
  );

  return (
    <DashboardContentMaxWidth>
      <PageHeader title={t(isCreatePage ? 'create-team' : 'edit-team')}>
        {isCreatePage ? undefined : (
          <Button
            buttonText={t('view')}
            buttonType={ButtonTypes.primaryOutlined}
            leftIcon="visibility"
            onClick={() => onViewChange(TeamViewMode.ViewDetails)}
          />
        )}
      </PageHeader>
      <PageBody>
        <>
          <WhiteBox verticalPaddings="large">
            <Form
              actionsComponent={actionsComponent}
              actionsComponentProps={{ isCreatePage }}
              defaultValues={defaultValues}
              fields={fields}
              formId="team"
              formRef={formRef}
              graphQLErrors={getGraphQLErrors(mutationError)}
              loading={loading}
              onSubmit={onSubmit}
            />
          </WhiteBox>
          {isDestroyAvailable && (
            <div className={styles.actions}>
              <Button
                buttonText={t('delete-team')}
                buttonType={ButtonTypes.negativeOutlined}
                leftIcon="delete"
                leftIconClassName={materialSymbolsOutlined}
                onClick={onDelete}
              />
            </div>
          )}
        </>
      </PageBody>
      <DepartmentsModalTableMultiselect
        isOpen={isDepartmentsModalOpen}
        onClose={onCloseDepartmentsModal}
        onSelect={onDepartmentsSelect}
        formRef={formRef}
        fieldName={FieldNames.DepartmentLinks}
        selectedTabFilters={
          !isCreatePage && id
            ? [
                {
                  teamsId: {
                    grouping: FilterGroupingEnum.And,
                    predicate: { eq: id },
                  },
                },
              ]
            : undefined
        }
      />
    </DashboardContentMaxWidth>
  );
}
